/* eslint-disable react/jsx-no-constructed-context-values */
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { http } from '../../helpers';

const DashboardContext = createContext(null);
const DATE_PICKER_HOUR = 12;
const ANALYTICS_ROUTE = '/case-api/analytics/query';
const DATE_RANGE_FORMAT = 'YYYY-MM-DD';

const getDashboardDateConfig = () => {
    const maxDate = moment()
        .startOf('day')
        .hour(DATE_PICKER_HOUR);
    const minStartDate = maxDate
        .clone()
        .subtract(3, 'years')
        .startOf('year')
        .hour(DATE_PICKER_HOUR);
    const defaultStartDate = maxDate
        .clone()
        .startOf('month')
        .hour(DATE_PICKER_HOUR);

    return {
        defaultStartDate,
        maxDate,
        minStartDate,
    };
};

const getCurrentTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver';

const buildAnalyticsQueries = ({
    from,
    to,
    timezone,
    selectedClinic,
    selectedLab,
}) => {
    const filterPayload = {
        ...(selectedClinic ? { clinicIds: [selectedClinic] } : {}),
        ...(selectedLab ? { labIds: [selectedLab] } : {}),
    };

    return [
        {
            key: 'casesByCreatedDate',
            payload: {
                dimension: 'CREATED_DATE_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                ...filterPayload,
            },
        },
        {
            key: 'casesByClinic',
            payload: {
                dimension: 'CLINIC_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'casesByLab',
            payload: {
                dimension: 'LAB_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'participation',
            payload: {
                dimension: 'DISTINCT_CLINIC_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                ...filterPayload,
            },
        },
    ];
};

function DashboardProvider({ children }) {
    const [dateConfig] = useState(getDashboardDateConfig);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [timezone] = useState(getCurrentTimezone);
    const [selectedClinic, setSelectedClinic] = useState('');
    const [selectedLab, setSelectedLab] = useState('');
    const [selectedStartDate, setSelectedStartDate] = useState(dateConfig.defaultStartDate);
    const [selectedEndDate, setSelectedEndDate] = useState(dateConfig.maxDate);

    useEffect(() => {
        const queries = buildAnalyticsQueries({
            from: selectedStartDate.format(DATE_RANGE_FORMAT),
            to: selectedEndDate.clone().add(1, 'day').format(DATE_RANGE_FORMAT),
            selectedClinic,
            selectedLab,
            timezone,
        });
        let isActive = true;

        setLoading(true);
        setFailed(false);

        Promise.allSettled(
            queries.map(({ payload }) => http.post(ANALYTICS_ROUTE, payload)),
        ).then((results) => {
            if (!isActive) return;

            const hasFailure = results.some((result) => result.status === 'rejected');

            if (hasFailure) {
                setFailed(true);
                setAnalyticsData(null);
                setLoading(false);
                return;
            }

            const nextAnalyticsData = results.reduce((acc, result, index) => ({
                ...acc,
                [queries[index].key]: result.value.data,
            }), {});

            setAnalyticsData(nextAnalyticsData);
            setFailed(false);
            setLoading(false);
        }).catch(() => {
            if (!isActive) return;

            setFailed(true);
            setAnalyticsData(null);
            setLoading(false);
        });

        return () => {
            isActive = false;
        };
    }, [selectedClinic, selectedEndDate, selectedLab, selectedStartDate, timezone]);

    const onUpdateClinic = (clinic) => {
        setSelectedClinic(clinic);
    };

    const onUpdateLab = (lab) => {
        setSelectedLab(lab);
    };

    const onUpdateDateRange = (nextStartDate, nextEndDate) => {
        setSelectedStartDate(nextStartDate);
        setSelectedEndDate(nextEndDate);
    };

    const value = {
        analyticsData,
        defaultEndDate: dateConfig.maxDate,
        defaultStartDate: dateConfig.defaultStartDate,
        failed,
        loading,
        maxDate: dateConfig.maxDate,
        minStartDate: dateConfig.minStartDate,
        onUpdateClinic,
        onUpdateDateRange,
        onUpdateLab,
        selectedClinic,
        selectedEndDate,
        selectedLab,
        selectedStartDate,
        setLoading,
        timezone,
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
}

DashboardProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

function useDashboardContext() {
    const context = useContext(DashboardContext);

    if (!context) {
        throw new Error('useDashboardContext must be used within DashboardProvider');
    }

    return context;
}

export {
    DashboardProvider,
    useDashboardContext,
};
